//
// # Instance Tooltip
//
// Displays instance name and properties when hovering over layout instances

import type { Editor } from "./editor";
import {
  ScalarInstanceEntity,
  Segment,
} from "DrawCore/src/content/layout/instance";
import { ContentKind } from "DrawCore/src/content/kind";
import type { layout } from "GenAlphaModel";
import { Palette } from "DrawCore/src/style";

export class InstanceTooltip {
  private tooltipElement: HTMLDivElement | null = null;
  private currentHoveredInstance: ScalarInstanceEntity | Segment | null = null;
  private hoverTimeout: number | null = null;
  private readonly HOVER_DELAY_MS = 500; // Show tooltip after 500ms of hovering

  constructor(private readonly editor: Editor) {}

  // Update tooltip based on current mouse position
  updateHover = (): void => {
    const { editor } = this;

    // Only show tooltips for layout content
    if (editor.content.contentKind !== ContentKind.Layout) {
      this.hideTooltip();
      return;
    }

    // Hit test to find what we're hovering over
    const hoveredInstance = editor.content.whatdWeHit(
      editor.uiState.mousePos
    ) as ScalarInstanceEntity | Segment | null;

    // If we're hovering over the same instance, do nothing
    if (hoveredInstance === this.currentHoveredInstance) {
      return;
    }

    // Clear any existing hover timeout
    if (this.hoverTimeout !== null) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    // Update current hovered instance
    this.currentHoveredInstance = hoveredInstance;

    if (hoveredInstance) {
      // Start hover delay timer
      this.hoverTimeout = window.setTimeout(() => {
        this.showTooltip(hoveredInstance);
        this.hoverTimeout = null;
      }, this.HOVER_DELAY_MS);
    } else {
      // Not hovering over an instance, hide tooltip
      this.hideTooltip();
    }
  };

  private showTooltip = (instance: ScalarInstanceEntity | Segment): void => {
    // Get instance data
    const instanceData =
      instance instanceof ScalarInstanceEntity
        ? instance.data
        : instance.scalarData;

    const name = instanceData.name;
    const target = instanceData.target;

    // Get properties if available
    let properties: Array<{ key: string; value: unknown }> = [];
    if (target.kind === "bound" && target.layout.properties) {
      properties = target.layout.properties.map((prop) => ({
        key: prop.name,
        value: prop.value,
      }));
    }

    // Create or update tooltip element
    if (!this.tooltipElement) {
      this.tooltipElement = document.createElement("div");
      this.setupTooltipStyles();
      this.editor.overlays.add(this.tooltipElement);
    }

    // Build tooltip content
    let content = `<div class="tooltip-header">Instance: ${name}</div>`;

    if (target.kind === "bound") {
      const targetIdent = target.layout.ident;
      if (targetIdent.kind === "libcellview") {
        content += `<div class="tooltip-target">Cell: ${targetIdent.lib}.${targetIdent.cell}</div>`;
      } else {
        content += `<div class="tooltip-target">Cell: ${JSON.stringify(
          targetIdent
        )}</div>`;
      }
    } else {
      // Handle unbound target
      if ("ident" in target) {
        // Safety comment for `as any` usage:
        // (a) targetIdent is actually an Ident type from the schema
        // (b) TypeScript's type narrowing isn't working properly with the union types here,
        //     but we know target.ident exists because we checked "ident" in target
        // (c) The any lifetime ends after we check targetIdent.kind and access its properties
        const targetIdent = target.ident as any;
        if (targetIdent.kind === "libcellview") {
          content += `<div class="tooltip-target">Target: ${targetIdent.lib}.${targetIdent.cell} (unbound)</div>`;
        } else {
          content += `<div class="tooltip-target">Target: ${JSON.stringify(
            targetIdent
          )} (unbound)</div>`;
        }
      }
    }

    if (properties.length > 0) {
      content += `<div class="tooltip-properties">`;
      content += `<div class="tooltip-section-header">Properties:</div>`;
      properties.forEach((prop) => {
        content += `<div class="tooltip-property">${prop.key}: ${prop.value}</div>`;
      });
      content += `</div>`;
    }

    this.tooltipElement.innerHTML = content;
    this.positionTooltip();
    this.tooltipElement.style.display = "block";
  };

  private hideTooltip = (): void => {
    if (this.tooltipElement) {
      this.tooltipElement.style.display = "none";
    }
    this.currentHoveredInstance = null;
  };

  private setupTooltipStyles = (): void => {
    if (!this.tooltipElement) return;

    // Apply theme-aware styles
    this.applyTooltipStyles();

    // Add CSS classes for internal styling (will be updated on theme change)
    this.styleSheet = document.createElement("style");
    this.updateStyleSheet();
    document.head.appendChild(this.styleSheet);
  };

  // Style sheet element for dynamic theme updates
  private styleSheet: HTMLStyleElement | null = null;

  // Apply inline styles to tooltip element
  private applyTooltipStyles = (): void => {
    if (!this.tooltipElement) return;

    const p = Palette.getAll();
    const style = this.tooltipElement.style;
    style.position = "absolute";
    style.backgroundColor = p.background;
    style.color = p.text;
    style.border = `1px solid ${p.border}`;
    style.borderRadius = "4px";
    style.padding = "8px 12px";
    style.fontSize = "12px";
    style.fontFamily = "Monaco, 'Courier New', monospace";
    style.maxWidth = "300px";
    style.zIndex = "1000";
    style.pointerEvents = "none";
    style.boxShadow = p.boxShadow;
    style.display = "none";
  };

  // Update the stylesheet with current theme colors
  private updateStyleSheet = (): void => {
    if (!this.styleSheet) return;

    const p = Palette.getAll();
    this.styleSheet.textContent = `
      .tooltip-header {
        font-weight: bold;
        margin-bottom: 4px;
        color: ${p.textHeader};
      }
      .tooltip-target {
        color: ${p.textTarget};
        margin-bottom: 4px;
        font-size: 11px;
      }
      .tooltip-properties {
        margin-top: 6px;
      }
      .tooltip-section-header {
        font-weight: bold;
        margin-bottom: 2px;
        color: ${p.textSection};
      }
      .tooltip-property {
        margin-left: 8px;
        font-size: 11px;
        color: ${p.textParam};
      }
    `;
  };

  // Update styles when theme changes (call from editor's theme change handler)
  updateTheme = (): void => {
    this.applyTooltipStyles();
    this.updateStyleSheet();
  };

  private positionTooltip = (): void => {
    if (!this.tooltipElement) return;

    const mousePos = this.editor.uiState.mousePos.page;
    const offset = 10; // Offset from mouse cursor

    // Position tooltip to the right and below the cursor
    this.tooltipElement.style.left = `${mousePos.x + offset}px`;
    this.tooltipElement.style.top = `${mousePos.y + offset}px`;

    // Ensure tooltip doesn't go off-screen
    const rect = this.tooltipElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Adjust horizontal position if tooltip would go off right edge
    if (rect.right > viewportWidth) {
      this.tooltipElement.style.left = `${mousePos.x - rect.width - offset}px`;
    }

    // Adjust vertical position if tooltip would go off bottom edge
    if (rect.bottom > viewportHeight) {
      this.tooltipElement.style.top = `${mousePos.y - rect.height - offset}px`;
    }
  };

  // Clean up resources
  dispose = (): void => {
    if (this.hoverTimeout !== null) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
    if (this.tooltipElement) {
      this.tooltipElement.remove();
      this.tooltipElement = null;
    }
    this.currentHoveredInstance = null;
  };
}
